import { types, type Instance, flow } from 'mobx-state-tree';

import type { PageConfig } from 'src/libs/PageConfig';
import currentUserService from 'src/services/currentUserService';

import { UserLoadState } from './types';

export const User = types.model('User', {
  userId: types.maybeNull(types.number),
  name: types.string,
  email: types.string,
  advertiserName: types.string,
  showOnCreditHoldMessage: types.boolean,
  isCsView: types.boolean,
  canExportToCsv: types.boolean,
  canCreateJobs: types.boolean,
  canManageUsers: types.boolean,
});

const UserStore = types
  .model('UserStore', {
    userLoadState: types.optional(
      types.enumeration<UserLoadState>(
        'UserLoadState',
        Object.values(UserLoadState),
      ),
      UserLoadState.LOADING,
    ),
    user: types.optional(User, {
      userId: undefined,
      name: '',
      email: '',
      advertiserName: '',
      showOnCreditHoldMessage: false,
      isCsView: false,
      canCreateJobs: false,
      canManageUsers: false,
      canExportToCsv: false,
    }),
  })
  .actions((self) => ({
    setUserLoadState(state: UserLoadState) {
      self.userLoadState = state;
    },
  }))
  .actions((self) => {
    const load = flow(function* ({
      accountInfo,
      seekAdvertiserId,
      addCustomError,
    }: {
      accountInfo?: {
        user?: {
          id: string;
          name: string;
        } | null;
        advertiser?: {
          name: string;
          showOnCreditHoldMessage: boolean;
          permissionOptions: {
            canCreateJobs: boolean;
            canManageUsers: boolean;
          };
        } | null;
        identityContext?: {
          email: string;
          act?: { isCsView: boolean } | null;
        } | null;
      } | null;
      seekAdvertiserId: string;
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) {
      try {
        self.setUserLoadState(UserLoadState.LOADING);

        if (!accountInfo) {
          throw new Error('Could not load account info');
        } else if (!accountInfo.advertiser) {
          throw new Error('Could not load advertiser info');
        } else if (!accountInfo.user) {
          throw new Error('Could not load user info');
        } else if (!accountInfo.identityContext) {
          throw new Error('Could not load identity info');
        }

        const canExportToCsv: { hasAccess: boolean } =
          yield currentUserService.fetchHasAccessToExportToCsv(
            seekAdvertiserId,
            addCustomError,
          );

        self.user = User.create({
          advertiserName: accountInfo.advertiser.name,
          email: accountInfo.identityContext.email,
          name: accountInfo.user.name,
          canCreateJobs: accountInfo.advertiser.permissionOptions.canCreateJobs,
          canManageUsers:
            accountInfo.advertiser.permissionOptions.canManageUsers,
          showOnCreditHoldMessage:
            accountInfo.advertiser.showOnCreditHoldMessage,
          userId: accountInfo.user.id
            ? Number.parseInt(accountInfo.user.id, 10)
            : undefined,
          isCsView: accountInfo.identityContext.act?.isCsView ?? false,
          canExportToCsv: Boolean(canExportToCsv.hasAccess),
        });

        self.setUserLoadState(UserLoadState.AUTHENTICATED);
      } catch (error: any) {
        self.setUserLoadState(UserLoadState.ERROR);
        addCustomError({
          error,
          context: {
            activity: 'FETCH_CURRENT_USER',
          },
        });
      }
    });

    return {
      load,
    };
  });

export interface IUserStore extends Instance<typeof UserStore> {}
export interface IUser extends Instance<typeof User> {}

export default UserStore;
